import { BoundingBox, type Entity, Mat4, Quat, Vec3 } from 'playcanvas';

import type { Group } from './group';
import { Scene } from './scene';
import { Serializer } from './serializer';

enum ElementType {
    camera = 'camera',
    group = 'group',
    model = 'model',
    splat = 'splat',
    shadow = 'shadow',
    debug = 'debug',
    other = 'other'
}

const ElementTypeList = [
    ElementType.camera,
    ElementType.group,
    ElementType.model,
    ElementType.splat,
    ElementType.shadow,
    ElementType.debug,
    ElementType.other
];

// module-scope scratch, matching the convention in src/model.ts - moveWorld runs on
// every frame of a gizmo drag, so it must not allocate.
const worldMat = new Mat4();
const parentInv = new Mat4();
const localMat = new Mat4();
const scratchQuat = new Quat();
const scratchPos = new Vec3();
const scratchScale = new Vec3();

let nextUid = 1;

class Element {
    type: ElementType;
    scene: Scene = null;
    uid: number;

    // Owning group, or null when the element sits at the scene root. Set only via
    // scene.attachEntity/detachEntity and the reparent edit op - never assigned directly,
    // so the entity graph and this field cannot disagree.
    parent: Group | null = null;

    constructor(type: ElementType) {
        this.type = type;
        this.uid = nextUid++;
    }

    destroy() {
        if (this.scene) {
            this.scene.remove(this);
        }
    }

    add(): void | Promise<void> {}

    remove() {}

    serialize(serializer: Serializer) {}

    onUpdate(deltaTime: number) {}

    onPostUpdate() {}

    onPreRender() {}

    onPostRender() {}

    onAdded(element: Element) {}

    onRemoved(element: Element) {}

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {}

    // Read this element's transform in world space. Local and world coincide only when
    // the element sits at the scene root, which was true of every element before groups.
    getWorldTransformComponents(position: Vec3, rotation: Quat, scale: Vec3) {
        const entity = (this as any).entity as Entity;

        // No group parent: the entity hangs off contentRoot, which is always at identity.
        // Read the stored local components rather than decomposing the world matrix -
        // getRotation()/getScale() re-derive from the matrix and lose ~1e-8, which would
        // shift every ungrouped drag by a hair against the previous, local-space handler.
        // getScale() also returns unsigned column lengths, so it cannot represent a
        // mirrored element at all. This is the read-side twin of moveWorld's early-out.
        if (!this.parent || !entity?.parent) {
            position.copy(entity.getLocalPosition());
            rotation.copy(entity.getLocalRotation());
            scale.copy(entity.getLocalScale());
            return;
        }

        position.copy(entity.getPosition());
        rotation.copy(entity.getRotation());
        scale.copy(entity.getScale());
    }

    // Set this element's transform from world-space values, converting through the
    // parent's inverse world matrix. Delegates to move(), which sets the local transform
    // and fires the element's own moved event - so ops and serialisation stay local.
    moveWorld(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const entity = (this as any).entity as Entity;
        const parentEntity = entity?.parent;

        // No group parent: the entity hangs off contentRoot, which is always at identity,
        // so world equals local. Forward the caller's own objects to move() untouched -
        // decomposing and recomposing them here would perturb the values.
        if (!this.parent || !parentEntity) {
            this.move(position, rotation, scale);
            return;
        }

        // Fill in any component the caller omitted from the element's current world state.
        worldMat.setTRS(
            position ?? entity.getPosition(),
            rotation ?? entity.getRotation(),
            scale ?? entity.getScale()
        );

        parentInv.copy(parentEntity.getWorldTransform()).invert();
        localMat.mul2(parentInv, worldMat);

        scratchQuat.setFromMat4(localMat);
        localMat.getScale(scratchScale);
        // getScale returns unsigned lengths while setFromMat4 negates a column on a
        // negative determinant, so a mirrored parent would otherwise un-mirror the child.
        scratchScale.x *= localMat.scaleSign;

        this.move(localMat.getTranslation(scratchPos), scratchQuat, scratchScale);
    }

    get worldBound(): BoundingBox | null {
        return null;
    }
}

export { ElementType, ElementTypeList, Element };
